/**
 * Cookie consent: which visitors must opt in, the choice they saved, and the
 * tool groups that may run. Essential items (sign-in, saved progress, error
 * reports, the choice itself) need no consent, so they are not a group.
 *
 * The choice lives in one first-party cookie for 12 months. It holds the two
 * groups, when it was saved and the version of the group list. A choice saved
 * under another version, or more than 12 months ago, counts as no choice.
 *
 * Aliases: cookie consent, cookie banner, tracking consent, dcm_consent,
 * consent groups, opt-in regions, Global Privacy Control
 */

import { z } from "zod";

export const CONSENT_COOKIE_NAME = "dcm_consent";

/**
 * Raise it when a tool moves into a group or a group changes meaning: every
 * visitor is then asked again.
 */
export const CONSENT_VERSION = 2;

export const CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

/**
 * Sets the visitor's region on the hermetic E2E server, as `DE`, or `CA-QC`
 * for a country with its region code. Every other server ignores it.
 */
export const E2E_REGION_HEADER = "X-E2E-Region";

export type ConsentGroup = "analytics" | "marketing";

export type ConsentGroups = Readonly<Record<ConsentGroup, boolean>>;

export const consentGroupsSchema = z.object({
  analytics: z.boolean(),
  marketing: z.boolean(),
}) satisfies z.ZodType<ConsentGroups>;

export interface ConsentChoice extends ConsentGroups {
  /** Milliseconds since the epoch. */
  readonly savedAt: number;
}

/** What the server knows about a visitor's consent when the page loads. */
export interface ConsentState {
  /** The visitor's region asks for opt-in, or the region is unknown. */
  readonly optInRegion: boolean;
  /** The browser sends Global Privacy Control (`Sec-GPC: 1`). */
  readonly gpc: boolean;
  readonly choice: ConsentChoice | null;
}

export const NO_CONSENT: ConsentGroups = { analytics: false, marketing: false };

export const FULL_CONSENT: ConsentGroups = { analytics: true, marketing: true };

/** EU members, including outermost regions that have their own code. */
const EU_COUNTRIES = [
  "AT",
  "BE",
  "BG",
  "CY",
  "CZ",
  "DE",
  "DK",
  "EE",
  "ES",
  "FI",
  "FR",
  "GR",
  "HR",
  "HU",
  "IE",
  "IT",
  "LT",
  "LU",
  "LV",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SE",
  "SI",
  "SK",
  // Åland, French Guiana, Guadeloupe, Martinique, Mayotte, Réunion and
  // Saint Martin are EU territory with their own ISO code.
  "AX",
  "GF",
  "GP",
  "MQ",
  "YT",
  "RE",
  "MF",
];

const OPT_IN_COUNTRIES = new Set([
  ...EU_COUNTRIES,
  // The rest of the EEA.
  "IS",
  "LI",
  "NO",
  "GB",
  "CH",
]);

/** Cloudflare's codes for an unknown country and for Tor. */
const UNKNOWN_COUNTRIES = new Set(["XX", "T1"]);

export interface RequestLocation {
  readonly country?: string | null;
  readonly regionCode?: string | null;
}

/**
 * Whether a visitor must opt in before analytics and marketing tools run: the
 * EU and EEA, the UK, Switzerland and Quebec. An unknown country, or a
 * Canadian province the request does not name, counts as opt-in.
 */
export function isOptInLocation({
  country,
  regionCode,
}: RequestLocation): boolean {
  if (!country || UNKNOWN_COUNTRIES.has(country)) return true;
  if (country === "CA") return !regionCode || regionCode === "QC";
  return OPT_IN_COUNTRIES.has(country);
}

const CHOICE_PATTERN = /^v(\d+)\.a([01])\.m([01])\.t(\d+)$/;

/** `v2.a1.m0.t1792479573000`: version, analytics, marketing, saved at. */
export function serializeConsentChoice(choice: ConsentChoice): string {
  const flag = (allowed: boolean): string => (allowed ? "1" : "0");
  return `v${CONSENT_VERSION}.a${flag(choice.analytics)}.m${flag(choice.marketing)}.t${choice.savedAt}`;
}

/**
 * The saved choice, or null when the cookie is missing, malformed, from
 * another version of the group list or older than 12 months.
 */
export function parseConsentChoice(
  value: string | null | undefined,
  now: number,
): ConsentChoice | null {
  const match = value?.match(CHOICE_PATTERN);
  if (!match) return null;
  const [, version, analytics, marketing, savedAt] = match;
  if (Number(version) !== CONSENT_VERSION) return null;
  const saved = Number(savedAt);
  if (now - saved >= CONSENT_MAX_AGE_SECONDS * 1000) return null;
  return {
    analytics: analytics === "1",
    marketing: marketing === "1",
    savedAt: saved,
  };
}

/**
 * The groups that may run. A saved choice always wins. Without one, opt-in
 * regions run nothing, and elsewhere everything runs except marketing tools
 * for a browser that sends Global Privacy Control.
 */
export function allowedConsentGroups({
  optInRegion,
  gpc,
  choice,
}: ConsentState): ConsentGroups {
  if (choice) {
    return { analytics: choice.analytics, marketing: choice.marketing };
  }
  if (optInRegion) return NO_CONSENT;
  return { analytics: true, marketing: !gpc };
}

/** Whether going from `before` to `after` turns off a group that was on. */
export function withdrawsConsent(
  before: ConsentGroups,
  after: ConsentGroups,
): boolean {
  return (
    (before.analytics && !after.analytics) ||
    (before.marketing && !after.marketing)
  );
}

/**
 * The internal widgets GoHighLevel embeds (`/embed/*`) show no banner and run
 * no tools: they are too small for a banner and are not public pages.
 */
export function isEmbedPath(pathname: string): boolean {
  return pathname === "/embed" || pathname.startsWith("/embed/");
}
